import { nycCityOfficeSeo } from './cities/nycCityOfficeSeo';
import { sfCityOfficeSeo } from './cities/sfCityOfficeSeo';
import { nycFlatironNomadOfficeSeo } from './neighborhoods/nycFlatironNomad';
import { nycHudsonYardsOfficeSeo } from './neighborhoods/nycHudsonYards';
import { nycLowerManhattanOfficeSeo } from './neighborhoods/nycLowerManhattan';
import { nycMidtownOfficeSeo } from './neighborhoods/nycMidtown';
import { nycSohoTribecaOfficeSeo } from './neighborhoods/nycSohoTribeca';
import { nycUnionSquareOfficeSeo } from './neighborhoods/nycUnionSquare';
import { sfFinancialDistrictOfficeSeo } from './neighborhoods/sfFinancialDistrict';
import { sfHayesValleyOfficeSeo } from './neighborhoods/sfHayesValley';
import { sfMissionBayOfficeSeo } from './neighborhoods/sfMissionBay';
import { sfPotreroHillOfficeSeo } from './neighborhoods/sfPotreroHill';
import { sfSomaOfficeSeo } from './neighborhoods/sfSoma';

import type { NeighborhoodOfficeSeoData } from './types';

export interface OfficeSeoHubEntry {
  locationSlug: string;
  neighborhoodData: NeighborhoodOfficeSeoData;
  targetKeyword: string;
  searchVolume: string;
  isCity: boolean;
  siblingLocationSlugs: readonly string[];
  cityHubSlug: string | null;
}

const SF_NEIGHBORHOOD_SLUGS = [
  'soma',
  'financial-district-sf',
  'mission-bay-sf',
  'potrero-hill-sf',
  'hayes-valley-sf'
] as const;

const NYC_NEIGHBORHOOD_SLUGS = [
  'midtown-nyc',
  'flatiron-nomad-nyc',
  'soho-tribeca-nyc',
  'hudson-yards-nyc',
  'lower-manhattan-nyc',
  'union-square-nyc'
] as const;

export const OFFICE_SEO_HUB_DATA: Record<string, OfficeSeoHubEntry> = {
  'san-francisco': {
    locationSlug: 'san-francisco',
    neighborhoodData: sfCityOfficeSeo,
    targetKeyword: 'office management services San Francisco',
    searchVolume: '3,600/mo',
    isCity: true,
    siblingLocationSlugs: [],
    cityHubSlug: null
  },
  soma: {
    locationSlug: 'soma',
    neighborhoodData: sfSomaOfficeSeo,
    targetKeyword: 'office management services SoMa',
    searchVolume: '480/mo',
    isCity: false,
    siblingLocationSlugs: SF_NEIGHBORHOOD_SLUGS.filter(s => s !== 'soma'),
    cityHubSlug: 'san-francisco'
  },
  'financial-district-sf': {
    locationSlug: 'financial-district-sf',
    neighborhoodData: sfFinancialDistrictOfficeSeo,
    targetKeyword: 'office management services Financial District',
    searchVolume: '480/mo',
    isCity: false,
    siblingLocationSlugs: SF_NEIGHBORHOOD_SLUGS.filter(s => s !== 'financial-district-sf'),
    cityHubSlug: 'san-francisco'
  },
  'mission-bay-sf': {
    locationSlug: 'mission-bay-sf',
    neighborhoodData: sfMissionBayOfficeSeo,
    targetKeyword: 'office management services Mission Bay',
    searchVolume: '480/mo',
    isCity: false,
    siblingLocationSlugs: SF_NEIGHBORHOOD_SLUGS.filter(s => s !== 'mission-bay-sf'),
    cityHubSlug: 'san-francisco'
  },
  'potrero-hill-sf': {
    locationSlug: 'potrero-hill-sf',
    neighborhoodData: sfPotreroHillOfficeSeo,
    targetKeyword: 'office management services Potrero Hill',
    searchVolume: '480/mo',
    isCity: false,
    siblingLocationSlugs: SF_NEIGHBORHOOD_SLUGS.filter(s => s !== 'potrero-hill-sf'),
    cityHubSlug: 'san-francisco'
  },
  'hayes-valley-sf': {
    locationSlug: 'hayes-valley-sf',
    neighborhoodData: sfHayesValleyOfficeSeo,
    targetKeyword: 'office management services Hayes Valley',
    searchVolume: '480/mo',
    isCity: false,
    siblingLocationSlugs: SF_NEIGHBORHOOD_SLUGS.filter(s => s !== 'hayes-valley-sf'),
    cityHubSlug: 'san-francisco'
  },
  'new-york-city': {
    locationSlug: 'new-york-city',
    neighborhoodData: nycCityOfficeSeo,
    targetKeyword: 'office management services New York City',
    searchVolume: '4,200/mo',
    isCity: true,
    siblingLocationSlugs: [],
    cityHubSlug: null
  },
  'midtown-nyc': {
    locationSlug: 'midtown-nyc',
    neighborhoodData: nycMidtownOfficeSeo,
    targetKeyword: 'office management services Midtown',
    searchVolume: '390/mo',
    isCity: false,
    siblingLocationSlugs: NYC_NEIGHBORHOOD_SLUGS.filter(s => s !== 'midtown-nyc'),
    cityHubSlug: 'new-york-city'
  },
  'flatiron-nomad-nyc': {
    locationSlug: 'flatiron-nomad-nyc',
    neighborhoodData: nycFlatironNomadOfficeSeo,
    targetKeyword: 'office management services Flatiron / NoMad',
    searchVolume: '390/mo',
    isCity: false,
    siblingLocationSlugs: NYC_NEIGHBORHOOD_SLUGS.filter(s => s !== 'flatiron-nomad-nyc'),
    cityHubSlug: 'new-york-city'
  },
  'soho-tribeca-nyc': {
    locationSlug: 'soho-tribeca-nyc',
    neighborhoodData: nycSohoTribecaOfficeSeo,
    targetKeyword: 'office management services SoHo / Tribeca',
    searchVolume: '390/mo',
    isCity: false,
    siblingLocationSlugs: NYC_NEIGHBORHOOD_SLUGS.filter(s => s !== 'soho-tribeca-nyc'),
    cityHubSlug: 'new-york-city'
  },
  'hudson-yards-nyc': {
    locationSlug: 'hudson-yards-nyc',
    neighborhoodData: nycHudsonYardsOfficeSeo,
    targetKeyword: 'office management services Hudson Yards',
    searchVolume: '390/mo',
    isCity: false,
    siblingLocationSlugs: NYC_NEIGHBORHOOD_SLUGS.filter(s => s !== 'hudson-yards-nyc'),
    cityHubSlug: 'new-york-city'
  },
  'lower-manhattan-nyc': {
    locationSlug: 'lower-manhattan-nyc',
    neighborhoodData: nycLowerManhattanOfficeSeo,
    targetKeyword: 'office management services Lower Manhattan',
    searchVolume: '390/mo',
    isCity: false,
    siblingLocationSlugs: NYC_NEIGHBORHOOD_SLUGS.filter(s => s !== 'lower-manhattan-nyc'),
    cityHubSlug: 'new-york-city'
  },
  'union-square-nyc': {
    locationSlug: 'union-square-nyc',
    neighborhoodData: nycUnionSquareOfficeSeo,
    targetKeyword: 'office management services Union Square',
    searchVolume: '390/mo',
    isCity: false,
    siblingLocationSlugs: NYC_NEIGHBORHOOD_SLUGS.filter(s => s !== 'union-square-nyc'),
    cityHubSlug: 'new-york-city'
  }
};

/**
 * Returns the hub location slug for a given cityPathSegment + neighborhoodSlug pair,
 * or null if no hub page exists for that location.
 */
export function getHubLocationSlug(cityPathSegment: string, neighborhoodSlug: string): string | null {
  for (const [locationSlug, entry] of Object.entries(OFFICE_SEO_HUB_DATA)) {
    if (
      entry.neighborhoodData.cityPathSegment === cityPathSegment &&
      entry.neighborhoodData.neighborhoodSlug === neighborhoodSlug
    ) {
      return locationSlug;
    }
  }
  return null;
}
